<template>
  <div class="tw-flex tw-items-center tw-gap-4 tw-bg-white">
    <div
      v-if="showSegmentStatus"
      class="
        tw-flex
        tw-gap-1
        tw-items-center
        tw-rounded-lg
        tw-text-sf-secondary-light
        tw-bg-sf-neutral-subtle-2
        tw-ml-2
        tw-px-2
        tw-py-1.5
        tw-font-medium
        inline-alert-banner
      "
    >
      <div
        class="
          tw-uppercase
          tw-text
          tw-text-sf-secondary-lighter
          tw-text-xs
          tw-mr-1
          tw-text-nowrap
        "
      >
        {{ I18n.t('companies_overview.progress_v2.current_query') }}
      </div>
      <div v-if="segment" class="tw-font-medium tw-line-clamp-1">
        {{ segment.name }} <segment-icon :segment="segment" />
      </div>
      <div v-else class="tw-font-normal">
        {{ I18n.t('segments.modal.all_companies') }}
      </div>

      <div
        class="sf-c-text-darker tw-py-0.5 tw-px-2 sf-btn tw-rounded-md tw-ml-1"
        data-testid="select-segment-btn"
        @click="showSegmentModal"
      >
        <span>{{
          I18n.t('companies_overview.progress_v2.select_another')
        }}</span>
      </div>
    </div>
    <div
      class="
        tw-gap-1
        tw-flex
        tw-items-center
        tw-rounded-lg
        tw-text-sf-dark-dark
        tw-px-2
        tw-py-1.5
        inline-alert-banner
      "
      :class="classForInlineAlert"
    >
      <!-- Loading message -->
      <div
        v-if="loading || isSegmentLoading"
        class="tw-flex tw-items-center tw-text-nowrap"
      >
        <div class="sf-icon sf-spinner tw-mr-2" />
        <div v-dompurify-html="loadingMessage" />
      </div>

      <!-- Number of results message -->
      <div
        v-else
        v-dompurify-html="resultsMessage"
        data-testid="number-of-results-message"
        class="tw-py-0.5 tw-px-1 tw-line-clamp-1"
      />

      <!-- Refresh data button -->
      <div
        v-if="canRefreshSegmentData"
        class="sf-c-text-darker tw-py-0.5 tw-px-2 sf-btn tw-rounded-md"
        data-testid="refresh-data-btn"
        @click="refreshData"
      >
        <span>{{ I18n.t('companies_overview.progress_v2.refresh') }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import { useDrawer } from '@app/shared/pinia/drawer'
import { mapState as mapPiniaState } from 'pinia'
import { mapState, mapGetters, mapActions } from 'vuex'
import { SfTime } from '@app/adapters/time'
import { SfLog } from '@app/adapters/logger'
import SegmentIcon from '../segments/segment-icon.vue'

export default {
  name: 'InsightsCompaniesListStatus',
  components: { SegmentIcon },
  computed: {
    ...mapState({
      companiesFullyLoaded: (state) => state.companies.companiesFullyLoaded,
      companiesFullyLoadedAt: (state) => state.companies.companiesFullyLoadedAt,
      segment: (state) => state.segments.segment,
      triggers: (state) => state.triggers.triggers,
      isSegmentLoading: (state) => state.segments.isSegmentLoading,
      isLite: (state) => state.lite,
      activeFilters: (state) => state.filters.activeFilters,
      period: (state) => state.periodSelection,
      searchTerm: (state) => state.companies.searchTerm,
      selectedCompanies: (state) => state.companies.selectedCompanies,
    }),
    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen']),
    ...mapGetters([
      'companiesProcessedPercentage',
      'allCompaniesProcessed',
      'allCompaniesCount',
      'sortedAndFilteredCompanies',
    ]),

    loading() {
      return !this.allCompaniesProcessed
    },
    classForInlineAlert() {
      return {
        'tw-bg-yellow-50 tw-text-sf-warning-darker': this.loading,
        'tw-bg-sf-neutral-subtle-2': !this.loading,
      }
    },
    loadingMessage() {
      return I18n.t('companies_overview.progress_v2.loading_html', {
        percentage: this.companiesProcessedPercentage,
      })
    },

    resultsMessage() {
      if (this.resultCompaniesSize === 0) {
        return I18n.t('companies_overview.progress_v2.no_clients_files_matched')
      }

      if (!this.segment && this.activeFilters.length === 0 && !this.period) {
        return I18n.t(
          'companies_overview.progress_v2.all_clients',
          this.resultMessageParams
        )
      }

      const message = I18n.t(
        'companies_overview.progress_v2.n_client_files_filtered',
        this.resultMessageParams
      )

      if (!this.companiesFullyLoaded) {
        const loadingFileData = I18n.t(
          'companies_overview.progress_v2.loading_file_data'
        )

        return `${message}, ${loadingFileData}`
      }

      if (!this.cachedResultsAge) {
        return message
      }

      const ageMessage = I18n.t(
        'companies_overview.progress_v2.updated_x_ago',
        this.resultMessageParams
      )
      return `${message}, ${ageMessage}`
    },
    resultMessageParams() {
      return {
        resultCompaniesSize: this.resultCompaniesSize,
        allCompanyIdsSize: this.allCompaniesCount,
        age: this.cachedResultsAge,
      }
    },
    resultCompaniesSize() {
      return this.sortedAndFilteredCompanies.length
    },
    cachedResultsAge() {
      return this.companiesFullyLoadedAt
        ? SfTime.unix(this.companiesFullyLoadedAt).fromNow(true)
        : null
    },
    canRefreshSegmentData() {
      return (
        !this.loading &&
        (this.cachedResultsAge || this.resultCompaniesSize === 0)
      )
    },
    searchPlaceholder() {
      return I18n.t('companies_overview.progress_v2.search_placeholder')
    },
    showSegmentStatus() {
      return !this.isLeftDrawerOpen
    },
  },
  methods: {
    ...mapActions(['fetchFilteredCompanies', 'showSegmentModal']),
    async refreshData() {
      const searchParams = new URLSearchParams(window.location.search)
      const requestRef = searchParams.get('requestRef')

      try {
        await this.fetchFilteredCompanies({
          existingRequestRef: requestRef,
          noCache: true,
        })
      } catch (e) {
        SfLog.warn('Error requesting Insights companies when refreshing', {
          extra: {
            firmId: window.Silverfin.currentFirmId,
            error: e,
          },
        })
      }
    },
  },
}
</script>
